import { useState, useEffect } from 'react';
import useSWR, { mutate as swrMutateByKey } from 'swr';

import { FOCUS_THROTTLE_INTERVAL, FIVE_MINUTES } from '@/config/appSettings';

import { IResponseError } from '@/lib/apiBase';
import { IRepository } from '@/types';
import { IRemoteWorkspace } from '@/lib/workspaces/workspaceApi';
import { fetchStagedFiles, TStaged } from '@/lib/workspaces/filesApi';

const SWR_STAGED_FILES_KEY = 'workspace_staged_files';

interface useRemoteWorkspacesParams {
  repository: IRepository;
  workspace: IRemoteWorkspace;
}

export const revalidateStagedFiles = ({
  namespace,
  repositoryName,
  workspaceId,
}: {
  namespace: string;
  repositoryName: string;
  workspaceId: string;
}) => {
  const swrKey = [SWR_STAGED_FILES_KEY, namespace, repositoryName, workspaceId];
  swrMutateByKey(swrKey);
};

const useStagedFiles = ({ repository, workspace }: useRemoteWorkspacesParams) => {
  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [error, setError] = useState<IResponseError | undefined>();
  const [stagedFiles, setStagedFiles] = useState<TStaged | undefined>();

  const fetchKey = [SWR_STAGED_FILES_KEY, repository.namespace, repository.name, workspace.id];

  const {
    data,
    error: requestError,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(
    fetchKey,
    () =>
      fetchStagedFiles({
        repository,
        workspaceId: workspace.id,
        resourcePath: workspace.resource_path,
      }),
    {
      keepPreviousData: true,
      refreshInterval: FIVE_MINUTES,
      revalidateOnFocus: false,
      focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
    },
  );

  useEffect(() => {
    if (data) {
      setStagedFiles(data?.staged);
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  useEffect(() => {
    if (requestError) {
      setError(requestError as IResponseError);
    }
  }, [requestError]);

  return {
    status,
    isLoading,
    isValidating,
    staged: stagedFiles,
    error,
  };
};

export default useStagedFiles;
